/**
 * Reportes → Descuentos y recargos activos (`/reportes/descuentos-activos`).
 *
 * Puerto de `Reportes::reporte_descuento_clientes_activos` +
 * `Reportes_model::get_descuentos_clientes_activos_por_empresa` (con
 * `get_descuentos_punto_playa_activos_por_cliente`, `get_descuentos_credito_activos` y
 * `filter_discount_items`) + `views/menu/reportes/reporte_descuento_clientes_activos.php`.
 *
 * La pantalla del panel anterior listaba, por cliente, **todos sus descuentos vigentes**
 * (una tarjeta por cliente con sus placas y sus productos), con los filtros de **fuente**
 * (punto de venta o crédito) y **tipo** (documento, placa, consumo, crédito o recargos) y
 * un filtro por cliente.
 *
 * Reglas conservadas:
 * - Los descuentos del punto de venta salen de `customer_discounts` (`status = 1`, tipos
 *   `document`, `license_plate` y `consumption`, y `valid_until` vigente o nula); el
 *   importe es `ABS(discount_amount)` y un importe **negativo** se muestra como
 *   **recargo**.
 * - Los del crédito salen del JSON `credito.descuento` (`credito_estado_id = 1`,
 *   `estado = 1`, `aplicar_descuento = 'SI'` y JSON válido), con `recargo` cuando el
 *   importe es negativo.
 * - El tipo `recargo` del filtro es una **naturaleza**: marca los que suman al precio,
 *   vengan de donde vengan, como en el original.
 *
 * Diferencias anotadas:
 * - El original recorría **todos los clientes** lanzando dos consultas por cliente; aquí
 *   son dos consultas para todos y el agrupado se hace en la API.
 * - Se muestran las **placas activas** del cliente (`customer_vehicles` con `status = 1`)
 *   además de la placa del descuento, y el CSV y el PDF los sirve la API.
 * - Los importes son **por unidad** (por galón o por documento), no el total facturado;
 *   la pantalla lo advierte para que nadie los lea como el monto del descuento del mes.
 */

import { ArrowLeft, Download, FileText, Info, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatDateValue, formatNumber } from '@/lib/utils';

import {
  reportesApi,
  useCatalogosDescuentosActivos,
  useDescuentosActivos,
  type FiltrosDescuentosActivos,
} from '../reportes.api';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** El importe por unidad del descuento (son valores pequeños: 4 decimales). */
const UNIDAD = new Intl.NumberFormat('es-PE', {
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});

const ETIQUETAS_TIPO: Record<string, string> = {
  document: 'Documento',
  license_plate: 'Placa',
  consumption: 'Consumo',
  credito: 'Crédito',
  recargo: 'Recargos',
};

export function ReporteDescuentosActivosPage() {
  useDocumentTitle('Reportes · Descuentos y recargos activos');

  const [filtros, setFiltros] = useState<FiltrosDescuentosActivos>({
    cliente: '',
    fuentes: [],
    tipos: [],
  });
  const [aviso, setAviso] = useState<string | null>(null);

  const catalogos = useCatalogosDescuentosActivos();
  const consulta = useDescuentosActivos(filtros);
  const datos = consulta.data;
  const resumen = datos?.resumen;

  const clientes = datos?.clientes ?? [];
  const porFuente = datos?.por_fuente ?? [];
  const porTipo = datos?.por_tipo ?? [];
  const opcionesClientes = catalogos.data?.clientes ?? [];

  const cambiar = <C extends keyof FiltrosDescuentosActivos>(
    campo: C,
    valor: FiltrosDescuentosActivos[C],
  ) => setFiltros((previos) => ({ ...previos, [campo]: valor }));

  const alternar = (campo: 'fuentes' | 'tipos', valor: string, marcado: boolean) =>
    setFiltros((previos) => ({
      ...previos,
      [campo]: marcado
        ? [...previos[campo], valor]
        : previos[campo].filter((actual) => actual !== valor),
    }));

  const exportar = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.exportarDescuentosActivos(filtros), {
        nombre: 'descuentos-activos.csv',
      });
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo exportar el reporte.');
    }
  };

  const exportarPdf = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.pdfDescuentosActivos(filtros));
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo generar el PDF.');
    }
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link
            to="/reportes"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:underline"
          >
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            Reportes
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-ink-900">Descuentos y recargos activos</h1>
          <p className="mt-1 text-sm text-ink-500">
            Los descuentos vigentes de cada cliente: los del punto de venta y los de su crédito.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={exportarPdf}
            leftIcon={<FileText className="size-4" aria-hidden="true" />}
          >
            PDF
          </Button>

          <Button
            variant="outline"
            onClick={exportar}
            leftIcon={<Download className="size-4" aria-hidden="true" />}
          >
            Exportar
          </Button>
          <Button
            variant="outline"
            onClick={() => void consulta.refetch()}
            leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
          >
            Actualizar
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1.5 text-sm sm:col-span-2">
              <span className="font-medium text-ink-700">
                Cliente{' '}
                <span className="font-normal text-ink-400">
                  {catalogos.data ? `(${formatNumber(opcionesClientes.length)} con descuentos)` : ''}
                </span>
              </span>
              <Select
                value={filtros.cliente}
                onChange={({ target }) => cambiar('cliente', target.value)}
                className={CONTROL}
                aria-label="Cliente"
                searchable
              >
                <option value="">TODOS</option>
                {opcionesClientes.map((cliente) => (
                  <option key={cliente.id} value={String(cliente.id)}>
                    {cliente.cliente} · {cliente.documento ?? 'sin documento'} ({cliente.items})
                  </option>
                ))}
              </Select>
            </label>

            <div className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Fuente</span>
              <div className="flex flex-wrap items-center gap-1.5">
                {(catalogos.data?.fuentes ?? []).map((fuente) => {
                  const marcada = filtros.fuentes.includes(fuente.valor);

                  return (
                    <button
                      key={fuente.valor}
                      type="button"
                      aria-pressed={marcada}
                      onClick={() => alternar('fuentes', fuente.valor, !marcada)}
                      className={cn(
                        'rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors',
                        marcada
                          ? 'border-brand-300 bg-brand-50 text-brand-700'
                          : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-700',
                      )}
                    >
                      {fuente.etiqueta}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Tipo</span>
              <div className="flex flex-wrap items-center gap-1.5">
                {(catalogos.data?.tipos ?? []).map((tipo) => {
                  const marcado = filtros.tipos.includes(tipo.valor);

                  return (
                    <button
                      key={tipo.valor}
                      type="button"
                      aria-pressed={marcado}
                      onClick={() => alternar('tipos', tipo.valor, !marcado)}
                      className={cn(
                        'rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors',
                        marcado
                          ? 'border-brand-300 bg-brand-50 text-brand-700'
                          : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-700',
                      )}
                      title={tipo.items > 0 ? `${formatNumber(tipo.items)} vigente(s)` : undefined}
                    >
                      {ETIQUETAS_TIPO[tipo.valor] ?? tipo.etiqueta}
                    </button>
                  );
                })}

                {filtros.fuentes.length > 0 || filtros.tipos.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => setFiltros((previos) => ({ ...previos, fuentes: [], tipos: [] }))}
                    className="ml-1 text-xs font-semibold text-ink-500 hover:text-brand-700 hover:underline"
                  >
                    Limpiar
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo cargar el reporte">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'Inténtalo de nuevo en unos segundos.'}
        </Alert>
      ) : null}

      {aviso ? (
        <Alert variant="error" title="No se pudo exportar">
          {aviso}
        </Alert>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
        {[
          {
            etiqueta: 'Clientes',
            valor: formatNumber(resumen?.clientes ?? 0),
            detalle: `${formatNumber(resumen?.placas ?? 0)} placa(s) con descuento`,
          },
          {
            etiqueta: 'Descuentos vigentes',
            valor: formatNumber(resumen?.items ?? 0),
            detalle: `${formatNumber(resumen?.descuentos ?? 0)} descuento(s)`,
          },
          {
            etiqueta: 'Recargos',
            valor: formatNumber(resumen?.recargos ?? 0),
            detalle: 'suman al precio',
          },
          {
            etiqueta: 'Descuento promedio',
            valor: UNIDAD.format(resumen?.descuento_promedio ?? 0),
            detalle: 'por unidad',
          },
          {
            etiqueta: 'Recargo promedio',
            valor: UNIDAD.format(resumen?.recargo_promedio ?? 0),
            detalle: 'por unidad',
          },
        ].map((tarjeta) => (
          <Card key={tarjeta.etiqueta}>
            <CardContent className="p-4">
              <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                {tarjeta.etiqueta}
              </p>
              <p className="text-lg font-bold text-ink-900">{tarjeta.valor}</p>
              <p className="text-xs text-ink-500">{tarjeta.detalle}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardContent className="space-y-3 p-4">
            <h2 className="text-sm font-semibold text-ink-800">Por fuente</h2>

            <div className="overflow-hidden rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">Fuente</th>
                    <th className="w-[6rem] px-2 py-2 text-right">Vigentes</th>
                    <th className="w-[7rem] px-2 py-2 text-right">Suma por unidad</th>
                  </tr>
                </thead>
                <tbody>
                  {porFuente.length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={3} className="px-2 py-6 text-center text-sm text-ink-500">
                        Sin descuentos que cumplan los filtros.
                      </td>
                    </tr>
                  ) : null}

                  {porFuente.map((fila) => (
                    <tr key={fila.fuente} className="border-t border-ink-100">
                      <td className="px-2 py-2 font-semibold text-ink-900">{fila.fuente}</td>
                      <td className="px-2 py-2 text-right text-ink-700">
                        {formatNumber(fila.items)}
                      </td>
                      <td className="px-2 py-2 text-right text-ink-700">
                        {UNIDAD.format(fila.suma)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-3 p-4">
            <h2 className="text-sm font-semibold text-ink-800">Por tipo</h2>

            <div className="overflow-hidden rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">Tipo</th>
                    <th className="w-[6rem] px-2 py-2 text-right">Vigentes</th>
                    <th className="w-[7rem] px-2 py-2 text-right">Suma por unidad</th>
                  </tr>
                </thead>
                <tbody>
                  {porTipo.length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={3} className="px-2 py-6 text-center text-sm text-ink-500">
                        Sin descuentos que cumplan los filtros.
                      </td>
                    </tr>
                  ) : null}

                  {porTipo.map((fila) => (
                    <tr key={fila.tipo} className="border-t border-ink-100">
                      <td className="px-2 py-2 font-semibold text-ink-900">
                        {ETIQUETAS_TIPO[fila.tipo] ?? fila.tipo}
                      </td>
                      <td className="px-2 py-2 text-right text-ink-700">
                        {formatNumber(fila.items)}
                      </td>
                      <td className="px-2 py-2 text-right text-ink-700">
                        {UNIDAD.format(fila.suma)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

      {!consulta.isLoading ? (
        <div className="grid gap-4 xl:grid-cols-2">
          {clientes.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-sm text-ink-500">
                No hay descuentos vigentes que cumplan los filtros.
              </CardContent>
            </Card>
          ) : null}

          {clientes.map((cliente) => (
            <Card key={cliente.cliente_id}>
              <CardContent className="space-y-3 p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <h2 className="text-sm font-semibold text-ink-900" title={cliente.cliente ?? ''}>
                      {cliente.cliente ?? '—'}
                    </h2>
                    <p className="text-xs text-ink-500">{cliente.documento ?? 'sin documento'}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant="brand" className="px-1.5 py-0 text-[10px]">
                      {formatNumber(cliente.items.length)} vigente(s)
                    </Badge>
                    {cliente.recargos > 0 ? (
                      <Badge variant="warning" className="px-1.5 py-0 text-[10px]">
                        {formatNumber(cliente.recargos)} recargo(s)
                      </Badge>
                    ) : null}
                  </div>
                </div>

                {cliente.placas_activas.length > 0 ? (
                  <p className="text-xs text-ink-500" title={cliente.placas_activas.join(', ')}>
                    <span className="font-semibold text-ink-700">Placas activas: </span>
                    {cliente.placas_activas.join(', ')}
                  </p>
                ) : cliente.placas.length > 0 ? (
                  <p className="text-xs text-ink-500">
                    <span className="font-semibold text-ink-700">Placas: </span>
                    {cliente.placas.join(', ')}
                  </p>
                ) : null}

                <div className="overflow-hidden rounded-card border border-ink-100">
                  <table className="w-full table-fixed border-collapse text-[13px]">
                    <thead className="bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                      <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                        <th className="px-1.5 py-2">Producto</th>
                        <th className="w-[6rem] px-1.5 py-2">Fuente</th>
                        <th className="w-[5.5rem] px-1.5 py-2">Tipo</th>
                        <th className="w-[5.5rem] px-1.5 py-2 text-right">Por unidad</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cliente.items.map((item) => (
                        <tr key={`${item.fuente}-${item.id}`} className="border-t border-ink-100">
                          <td className="px-1.5 py-2">
                            <p className="truncate font-semibold text-ink-800" title={item.producto ?? ''}>
                              {item.producto ?? '—'}
                            </p>
                            {item.placa ? (
                              <p className="truncate text-[11px] text-ink-500">placa {item.placa}</p>
                            ) : null}
                            {item.detalle ? (
                              <p className="truncate text-[11px] text-ink-400">{item.detalle}</p>
                            ) : null}
                            {item.vigente_hasta ? (
                              <p className="truncate text-[11px] text-ink-400">
                                hasta {formatDateValue(item.vigente_hasta)}
                              </p>
                            ) : null}
                          </td>
                          <td className="px-1.5 py-2">
                            <span className="text-[11px] text-ink-600">{item.fuente}</span>
                          </td>
                          <td className="px-1.5 py-2">
                            <Badge
                              variant={item.naturaleza === 'recargo' ? 'warning' : 'neutral'}
                              className="px-1.5 py-0 text-[10px]"
                            >
                              {ETIQUETAS_TIPO[item.tipo_descuento] ?? item.tipo_descuento}
                            </Badge>
                          </td>
                          <td
                            className={cn(
                              'px-1.5 py-2 text-right font-semibold',
                              item.naturaleza === 'recargo' ? 'text-amber-700' : 'text-emerald-700',
                            )}
                          >
                            {item.naturaleza === 'recargo' ? '+' : '−'}
                            {UNIDAD.format(item.descuento)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : null}

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        <span>
          Los importes son <strong>por unidad</strong> (por galón o por documento), no el total
          facturado del mes. Los descuentos del punto de venta vienen de sus reglas vigentes
          (`customer_discounts`) y los del crédito, del descuento configurado en el crédito; los que
          suman al precio se muestran como <strong>recargo</strong>.
        </span>
      </p>
    </div>
  );
}
